// Imported by path, not through the barrel: `shared/lib`'s index also pulls in
// hooks that import `shared/api`, which would close a cycle.
import { registerReset } from '@/shared/lib/reset-registry'

/**
 * The credential lives here and nowhere else — in memory, never in
 * `localStorage`. A cookie-based backend simply leaves the token null and the
 * interceptor still works, because requests always send credentials.
 *
 * How to obtain and refresh a credential is application knowledge, so the auth
 * feature injects those handlers at boot. `shared/` stays feature-blind.
 */

/** `ok` distinguishes "refreshed, cookie scheme, no bearer" from "refresh failed". */
export type RefreshResult = { ok: true; token: string | null } | { ok: false }

type RefreshHandler = () => Promise<RefreshResult>
type SessionLostHandler = () => void

const FAILED: RefreshResult = { ok: false }

let accessToken: string | null = null
let refreshHandler: RefreshHandler | null = null
let sessionLostHandler: SessionLostHandler | null = null

/** In-flight refresh. Concurrent 401s await this same promise. */
let refreshInFlight: Promise<RefreshResult> | null = null

export const authSession = {
  getToken: () => accessToken,

  setToken(token: string | null) {
    accessToken = token
  },

  /** Called once from the composition root. */
  configure(handlers: { refresh: RefreshHandler; onSessionLost: SessionLostHandler }) {
    refreshHandler = handlers.refresh
    sessionLostHandler = handlers.onSessionLost
  },

  /**
   * Runs at most one refresh at a time. Every caller that arrives while a
   * refresh is running gets the same answer instead of starting another.
   */
  refresh(): Promise<RefreshResult> {
    if (refreshInFlight !== null) return refreshInFlight

    const handler = refreshHandler
    if (handler === null) return Promise.resolve(FAILED)

    refreshInFlight = handler()
      .then((result) => {
        accessToken = result.ok ? result.token : null
        return result
      })
      .catch(() => FAILED)
      .finally(() => {
        refreshInFlight = null
      })

    return refreshInFlight
  },

  /** Refresh failed or the session is gone: drop everything and send to login. */
  sessionLost() {
    accessToken = null
    sessionLostHandler?.()
  },
}

registerReset(() => {
  accessToken = null
  refreshInFlight = null
})
